import { ApolloError, useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RESOLVE_PET_QR_QUERY } from '@/modules/customers-pets/customers.gql';
import { Input } from '@/shared/ui/input';

export interface PetQrHit {
  customer_id: string;
  customer_name: string;
  pet_id: string;
  pet_name: string;
}

interface ResolvePetQrResponse {
  resolvePetQrForStaff: PetQrHit | null;
}

interface PetQrScanBoxProps {
  on_resolve: (hit: PetQrHit) => void;
  disabled?: boolean;
}

/** Mirrors `BarcodeScanBox` — a plain text input a hardware QR/barcode scanner types into,
 * resolving only on Enter/submit (no debounce). */
export function PetQrScanBox({ on_resolve, disabled }: PetQrScanBoxProps): JSX.Element {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [resolved, setResolved] = useState<PetQrHit | null>(null);
  const [error_key, setErrorKey] = useState<string | null>(null);

  const [runLookup, { loading }] = useLazyQuery<ResolvePetQrResponse>(RESOLVE_PET_QR_QUERY, {
    fetchPolicy: 'network-only',
  });

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const trimmed = token.trim();
    if (trimmed === '') return;
    setErrorKey(null);
    setResolved(null);
    // A pet-QR token is a JWT: exactly three dot-separated segments. A hardware scanner that
    // dropped characters on a long payload usually lands here — fail fast with a clear message
    // instead of a round-trip that just returns `pet_qr_invalid`.
    if (trimmed.split('.').length !== 3) {
      setErrorKey('doctor_pos.pet_qr_invalid');
      return;
    }
    try {
      const { data } = await runLookup({ variables: { token: trimmed } });
      const hit = data?.resolvePetQrForStaff ?? null;
      if (!hit) {
        setErrorKey('doctor_pos.pet_qr_invalid');
        return;
      }
      setResolved(hit);
      setToken('');
      on_resolve(hit);
    } catch (err) {
      const code =
        err instanceof ApolloError ? (err.graphQLErrors[0]?.message ?? err.message) : null;
      setErrorKey(
        code === 'pet_qr_expired' ? 'doctor_pos.pet_qr_expired' : 'doctor_pos.pet_qr_invalid',
      );
    }
  };

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event);
      }}
      className="space-y-1"
    >
      <Input
        type="text"
        inputMode="text"
        autoComplete="off"
        placeholder={t('doctor_pos.pet_qr_scan_placeholder')}
        aria-label={t('doctor_pos.pet_qr_scan_label')}
        value={token}
        onChange={(event) => {
          setToken(event.target.value);
          if (error_key) setErrorKey(null);
        }}
        disabled={disabled === true || loading}
      />
      <p className="text-xs text-muted-foreground">{t('doctor_pos.pet_qr_scan_hint')}</p>
      {error_key ? <p className="text-destructive text-xs">{t(error_key)}</p> : null}
      {resolved ? (
        <p className="text-xs text-emerald-600">
          {t('doctor_pos.pet_qr_resolved', {
            customer_name: resolved.customer_name,
            pet_name: resolved.pet_name,
          })}
        </p>
      ) : null}
    </form>
  );
}
